import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Gift, Plus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { feedback, unlockSound } from "@/lib/qr";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Stamps } from "@/components/qr/Stamps";

function OfferQr({ offer, onClose }) {
  const [t, setT] = useState(null);
  const [left, setLeft] = useState(0);
  const [status, setStatus] = useState("pending");
  const fired = useRef(false);
  useEffect(() => {
    api.post(`/qr/offers/${offer.id}/token`).then((r) => { setT(r.data); setLeft(r.data.ttl); })
      .catch((e) => { feedback(false); toast.error(errMsg(e)); onClose(); });
  }, [offer.id, onClose]);
  useEffect(() => {
    if (!t || status !== "pending") return undefined;
    const i = setInterval(() => {
      setLeft((x) => Math.max(0, x - 2));
      api.get(`/qr/offer-token/${t.jti}`).then((r) => { if (r.data.tx_id) setT((x) => ({ ...x, tx_id: r.data.tx_id })); setStatus(r.data.status); }).catch(() => {});
    }, 2000);
    return () => clearInterval(i);
  }, [t, status]);
  useEffect(() => {
    if (status === "used" && !fired.current) { fired.current = true; feedback(true); }
  }, [status]);
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="offer-qr-dialog">
        <DialogHeader><DialogTitle>{offer.name}</DialogTitle></DialogHeader>
        {status === "used" ? (
          <div className="text-center py-4" data-testid="offer-qr-used"><CheckCircle2 className="w-20 h-20 text-emerald-500 mx-auto" /><div className="text-xl font-black mt-2">Shpërblimi u përdor!</div><div className="text-xs text-slate-500">ID {t?.tx_id}</div></div>
        ) : !t ? <p className="text-center text-sm text-slate-400 py-8">Duke përgatitur...</p> : left <= 0 ? (
          <p className="text-center text-sm text-rose-600 py-8" data-testid="offer-qr-expired">QR skadoi. Mbylle dhe provo përsëri.</p>
        ) : (
          <div className="text-center">
            <QRCodeSVG value={t.code} size={220} className="mx-auto" data-testid="offer-qr-code" />
            <p className="text-sm text-slate-600 mt-3">Tregoja këtë QR stafit të biznesit për ta skanuar.</p>
            <p className="text-xs text-slate-400 mt-1">Vlen edhe {Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")} · përdoret vetëm një herë</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function OfferCard({ o, children }) {
  return (
    <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 p-3" data-testid={`listing-offer-${o.id}`}>
      <div className="flex gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-amber-800 text-xs font-bold"><Gift className="w-4 h-4" />{o.visits_needed ? "Kartë besnikërie" : o.kind}</div>
          <div className="font-extrabold text-slate-900 mt-0.5 break-words">{o.name}</div>
          {o.value && <div className="text-sm font-bold text-emerald-700" data-testid={`offer-value-${o.id}`}>Vlera: {o.value}</div>}
          {o.description && <p className="text-xs text-slate-600 mt-0.5">{o.description}</p>}
        </div>
        {o.image && <img src={o.image} alt="" className="w-20 h-20 rounded-xl object-cover shrink-0" data-testid={`offer-img-${o.id}`} />}
      </div>
      <Stamps need={o.visits_needed} have={o.stamps} />
      <div className="text-[11px] text-slate-500 mt-1">{o.cost ? `${o.cost} kredite` : "Falas"}{o.min_points ? ` · min. ${o.min_points} kredite` : ""}{o.requires_visit ? " · kërkon vizitë në lokal" : ""} · {o.end ? `vlen deri më ${o.end}` : "pa afat"}</div>
      {children}
    </div>
  );
}

export function ListingOffers({ listingId, ownerId }) {
  const nav = useNavigate();
  const { user, requireAuth } = useAuth();
  const [offers, setOffers] = useState([]);
  const [open, setOpen] = useState(null);
  const close = useCallback(() => setOpen(null), []);
  useEffect(() => { api.get(`/listings/${listingId}/offers`).then((r) => setOffers(r.data || [])).catch(() => {}); }, [listingId]);
  const isOwner = user && String(user.id) === String(ownerId);
  if (!offers.length && !(isOwner && user.account_type === "business")) return null;
  const use = (o) => { if (!user) return requireAuth?.(); unlockSound(); setOpen(o); };
  return (
    <div className="mt-3 space-y-2" data-testid="listing-offers">
      {offers.map((o) => (
        <OfferCard key={o.id} o={o}>
          {!isOwner && (!o.visits_needed || o.stamps >= o.visits_needed) && <button data-testid={`offer-use-${o.id}`} onClick={() => use(o)} className="w-full mt-2 h-11 rounded-xl bg-amber-400 text-slate-900 font-bold active:scale-[0.98] transition-transform">Përdor shpërblimin</button>}
          {!isOwner && o.visits_needed > 0 && o.stamps < o.visits_needed && <p className="text-[11px] text-slate-500 mt-1.5">Skano QR-në e biznesit në lokal pas çdo vizite.</p>}
        </OfferCard>
      ))}
      {isOwner && user.account_type === "business" && <button data-testid="listing-offer-create" onClick={() => nav(`/qr?tab=biz&listing=${listingId}`)} className="w-full h-11 rounded-xl border-2 border-dashed border-amber-300 text-amber-800 text-sm font-bold flex items-center justify-center gap-1"><Plus className="w-4 h-4" />Krijo ofertë për këtë lokal</button>}
      {open && <OfferQr offer={open} onClose={close} />}
    </div>
  );
}
